import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Leaf, Send, Lock, CheckCircle2, ArrowLeft, Sparkles, FileText,
  Trash2, Clock, ChevronRight, Loader2, Search, X, ClipboardList
} from 'lucide-react';
import { COLORS, CHAT_SUGGESTIONS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { sendChatMessage, fetchChatHistory, generatePlan, fetchPlans, deletePlan } from '../services/api';

const PLAN_TOPICS = [
  'Improve my cardiovascular health',
  'Better sleep quality',
  'Reduce stress and anxiety',
  'VILPA exercise routine',
  'Mediterranean diet plan',
  'Weight loss for longevity',
];

export default function ChatPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState('chat'); // 'chat' | 'plans' | 'plan-detail' | 'generate'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genStage, setGenStage] = useState(''); // 'researching' | 'generating' | 'done'
  const bottomRef = useRef(null);
  const isElite = (user?.membership_tier || 0) >= 4;

  useEffect(() => {
    if (isElite) {
      fetchChatHistory('default')
        .then(data => {
          if (data.length === 0) {
            setMessages([{ role: 'assistant', content: "Hello! I'm your ThriveLonger.ai Health Coach. I can chat about health topics or create a personalized plan for you. What would you like to do?" }]);
          } else {
            setMessages(data.map(m => ({ role: m.role, content: m.content })));
          }
          setLoaded(true);
        })
        .catch(() => {
          setMessages([{ role: 'assistant', content: "Hello! I'm your ThriveLonger.ai Health Coach. How can I help you today?" }]);
          setLoaded(true);
        });
      loadPlans();
    }
  }, [isElite]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadPlans = useCallback(async () => {
    try { setPlans(await fetchPlans()); } catch {}
  }, []);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);
    try {
      const resp = await sendChatMessage(userMsg.content, 'default');
      setMessages(prev => [...prev, { role: 'assistant', content: resp.content }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    }
    setSending(false);
  };

  const handleGeneratePlan = async (topic) => {
    if (!topic.trim()) return;
    setView('generate');
    setGenerating(true);
    setGenTopic(topic);
    setGenStage('researching');
    // Simulate research phase then call API
    setTimeout(() => setGenStage('generating'), 2000);
    try {
      const resp = await generatePlan(topic, 'default');
      setGenStage('done');
      if (resp.plan) {
        setSelectedPlan(resp.plan);
      }
      setMessages(prev => [
        ...prev,
        { role: 'user', content: `Create a health plan for: ${topic}` },
        { role: 'assistant', content: resp.content },
      ]);
      loadPlans();
    } catch (e) {
      setGenStage('');
      alert('Failed to generate plan. Try again.');
      setView('chat');
    }
    setGenerating(false);
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await deletePlan(planId); loadPlans(); if (selectedPlan?.plan_id === planId) { setSelectedPlan(null); setView('plans'); } } catch {}
  };

  // Format plan content for display
  const formatMessage = (text) => {
    // Strip plan markers for display
    let display = text.replace(/---PLAN_START---/g, '').replace(/---PLAN_END---/g, '');
    return display.trim();
  };

  if (!isElite) {
    return (
      <div style={{ maxWidth: 840, margin: '0 auto', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 200px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, alignSelf: 'flex-start', marginBottom: 24 }}>
          <button data-testid="back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>AI Health Coach</h1>
        </div>
        <div style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#F3E5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <Lock size={48} color={COLORS.tierElite} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, textAlign: 'center', fontFamily: 'Outfit, sans-serif' }}>Elite Membership Required</h2>
        <p style={{ fontSize: 15, color: COLORS.textSecondary, textAlign: 'center', marginTop: 10, lineHeight: 1.5, maxWidth: 480 }}>AI coaching with GPT-4o is available exclusively for Tier 4 (Elite) members.</p>
        <button onClick={() => navigate('/dashboard')} style={{ marginTop: 24, padding: '12px 24px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Back to Dashboard</button>
        <button data-testid="open-group-chats-from-gate-btn" onClick={() => navigate('/dashboard/chat/groups')} style={{ marginTop: 10, padding: '10px 22px', borderRadius: 9999, border: `1px solid ${COLORS.primary}`, backgroundColor: COLORS.surface, color: COLORS.primary, fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ClipboardList size={15} /> Open Group Chats (all members)
        </button>
      </div>
    );
  }

  // Plan detail view
  if (view === 'plan-detail' && selectedPlan) {
    return (
      <div data-testid="plan-detail" style={{ maxWidth: 840, margin: '0 auto', padding: '24px' }}>
        <button data-testid="back-to-plans" onClick={() => { setSelectedPlan(null); setView('plans'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600 }}><ArrowLeft size={18} /> Back to Plans</button>
        <div style={{ backgroundColor: COLORS.surface, borderRadius: 20, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ClipboardList size={24} color={COLORS.primary} /></div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>{selectedPlan.title || selectedPlan.topic}</h2>
              <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 13, color: COLORS.textSecondary }}>
                {selectedPlan.category && <span style={{ padding: '2px 8px', borderRadius: 6, backgroundColor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 600, textTransform: 'capitalize' }}>{selectedPlan.category}</span>}
                {selectedPlan.duration && <span>{selectedPlan.duration}</span>}
                <span>{new Date(selectedPlan.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button onClick={() => handleDeletePlan(selectedPlan.plan_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}><Trash2 size={18} color={COLORS.error} /></button>
          </div>
          {selectedPlan.overview && <p style={{ fontSize: 15, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 20 }}>{selectedPlan.overview}</p>}
          {selectedPlan.week_by_week?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 10 }}>Week-by-Week Plan</h3>
              {selectedPlan.week_by_week.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < selectedPlan.week_by_week.length - 1 ? `1px solid ${COLORS.border}30` : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{i + 1}</div>
                  <p style={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.5, margin: 0 }}>{w}</p>
                </div>
              ))}
            </div>
          )}
          {selectedPlan.key_actions?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 10 }}>Key Actions</h3>
              {selectedPlan.key_actions.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}><CheckCircle2 size={16} color={COLORS.success} /><span style={{ fontSize: 14, color: COLORS.textPrimary }}>{a}</span></div>
              ))}
            </div>
          )}
          {selectedPlan.expected_outcomes && (
            <div style={{ backgroundColor: `${COLORS.success}08`, borderRadius: 12, padding: 16, border: `1px solid ${COLORS.success}20` }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.success, marginBottom: 6 }}>Expected Outcomes</h3>
              <p style={{ fontSize: 14, color: COLORS.textPrimary, lineHeight: 1.5, margin: 0 }}>{selectedPlan.expected_outcomes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Generating plan view
  if (view === 'generate') {
    return (
      <div data-testid="plan-generating" style={{ maxWidth: 840, margin: '0 auto', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 200px)' }}>
        <button data-testid="back-from-generate" onClick={() => { if (!generating) setView('chat'); }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start', marginBottom: 20, color: COLORS.textSecondary, fontSize: 14, fontWeight: 600, opacity: generating ? 0.4 : 1 }}><ArrowLeft size={18} /> Back to Chat</button>
        {genStage === 'done' && selectedPlan ? (
          <div style={{ textAlign: 'center', width: '100%', maxWidth: 480 }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: `${COLORS.success}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><CheckCircle2 size={36} color={COLORS.success} /></div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Plan Created!</h2>
            <p style={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary, marginTop: 8 }}>{selectedPlan.title}</p>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 4 }}>{selectedPlan.duration} · {selectedPlan.category}</p>
            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center' }}>
              <button data-testid="view-plan-btn" onClick={() => setView('plan-detail')} style={{ padding: '14px 24px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>View Full Plan</button>
              <button onClick={() => setView('chat')} style={{ padding: '14px 24px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Back to Chat</button>
            </div>
          </div>
        ) : genStage === 'done' ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={48} color={COLORS.success} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>Response Added to Chat</h2>
            <button onClick={() => setView('chat')} style={{ marginTop: 20, padding: '14px 24px', borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer' }}>View in Chat</button>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              {genStage === 'researching' ? <Search size={32} color={COLORS.primary} /> : <Sparkles size={32} color={COLORS.primary} />}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>
              {genStage === 'researching' ? 'Researching the Web...' : 'Generating Your Plan...'}
            </h2>
            <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 8, lineHeight: 1.5 }}>
              {genStage === 'researching'
                ? `Searching NIA, Stanford, and our research library for "${genTopic}"...`
                : 'Creating a personalized plan based on your health profile and latest research...'}
            </p>
            <div style={{ width: 200, height: 4, backgroundColor: COLORS.surfaceSecondary, borderRadius: 2, margin: '24px auto', overflow: 'hidden' }}>
              <div style={{ height: 4, backgroundColor: COLORS.primary, borderRadius: 2, animation: 'progressPulse 2s ease-in-out infinite', width: genStage === 'researching' ? '40%' : '80%', transition: 'width 1s ease' }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Saved plans view
  if (view === 'plans') {
    return (
      <div data-testid="saved-plans" style={{ maxWidth: 840, margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button data-testid="back-to-chat" onClick={() => setView('chat')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>My Health Plans</h1>
        </div>
        {plans.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <ClipboardList size={48} color={COLORS.border} />
            <h3 style={{ color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>No Plans Yet</h3>
            <p style={{ color: COLORS.textSecondary }}>Ask the AI Coach to generate a personalized plan for you.</p>
            <button onClick={() => setView('chat')} style={{ marginTop: 16, padding: '12px 24px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer' }}>Start Chatting</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {plans.map(p => (
              <button key={p.plan_id} data-testid={`plan-${p.plan_id}`} onClick={() => { setSelectedPlan(p); setView('plan-detail'); }} style={{ display: 'flex', alignItems: 'center', gap: 14, backgroundColor: COLORS.surface, borderRadius: 14, padding: 18, border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: `${COLORS.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ClipboardList size={22} color={COLORS.primary} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>{p.title || p.topic}</div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>{p.category} · {p.duration} · {new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <ChevronRight size={18} color={COLORS.textSecondary} />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Main chat view
  return (
    <div style={{ maxWidth: 840, margin: '0 auto', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
      <div style={{ padding: '12px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button data-testid="back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><ArrowLeft size={22} color={COLORS.textPrimary} /></button>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>AI Health Coach</h1>
        </div>
        <button data-testid="view-plans-btn" onClick={() => setView('plans')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9999, border: `1px solid ${COLORS.primary}30`, backgroundColor: `${COLORS.primary}08`, color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          <FileText size={14} /> My Plans {plans.length > 0 && `(${plans.length})`}
        </button>
        <button data-testid="open-group-chats-btn" onClick={() => navigate('/dashboard/chat/groups')} style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9999, border: `1px solid ${COLORS.primary}30`, backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          <ClipboardList size={14} /> Group Chats
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        {messages.length <= 1 && loaded && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '28px 0' }}>
            <Leaf size={44} color={COLORS.primary} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 14, fontFamily: 'Outfit, sans-serif' }}>Hello, {user?.name}!</h2>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, textAlign: 'center', marginTop: 6, lineHeight: 1.5 }}>Chat with me or generate a personalized health plan.</p>

            {/* Plan Generation Section */}
            <div style={{ width: '100%', maxWidth: 440, marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Generate a Plan</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {PLAN_TOPICS.map((t, i) => (
                  <button key={i} data-testid={`plan-topic-${i}`} onClick={() => handleGeneratePlan(t)} style={{ padding: '8px 14px', borderRadius: 9999, border: `1px solid ${COLORS.primary}30`, backgroundColor: `${COLORS.primary}06`, color: COLORS.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t}</button>
                ))}
              </div>
            </div>

            {/* Chat suggestions */}
            <div style={{ width: '100%', maxWidth: 440, marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textSecondary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Or ask me anything</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CHAT_SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => setInput(s)} style={{ backgroundColor: COLORS.surface, borderRadius: 12, padding: 14, border: `1px solid ${COLORS.border}`, cursor: 'pointer', textAlign: 'left', fontSize: 14, color: COLORS.primary, fontWeight: 500 }}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className="chat-bubble" style={{ display: 'flex', marginBottom: 12, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && (
              <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}><Leaf size={18} color={COLORS.primary} /></div>
            )}
            <div style={{ maxWidth: '75%', borderRadius: 16, padding: 12, backgroundColor: msg.role === 'user' ? COLORS.primary : COLORS.surfaceSecondary, borderBottomRightRadius: msg.role === 'user' ? 4 : 16, borderBottomLeftRadius: msg.role === 'assistant' ? 4 : 16 }}>
              <p style={{ fontSize: 15, color: msg.role === 'user' ? '#FFF' : COLORS.textPrimary, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>{formatMessage(msg.content)}</p>
            </div>
          </div>
        ))}

        {sending && (
          <div className="chat-bubble" style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8 }}><Leaf size={18} color={COLORS.primary} /></div>
            <div style={{ backgroundColor: COLORS.surfaceSecondary, borderRadius: 16, padding: '12px 20px' }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map(i => (<div key={i} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: COLORS.textSecondary, opacity: 0.4, animation: `fadeIn 0.6s ease ${i * 0.2}s infinite alternate` }} />))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input + Plan generate */}
      <div style={{ padding: '10px 24px 12px', borderTop: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Ask your health coach..." rows={1} style={{ flex: 1, backgroundColor: COLORS.background, borderRadius: 16, padding: '12px 16px', fontSize: 15, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}`, resize: 'none', maxHeight: 100, fontFamily: 'Work Sans, sans-serif' }} />
          <button data-testid="generate-plan-input-btn" onClick={() => { if (input.trim()) handleGeneratePlan(input.trim()); }} disabled={!input.trim() || sending} title="Generate a plan from this topic" style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: input.trim() ? COLORS.accentWarm : COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: input.trim() ? 'pointer' : 'default', opacity: (!input.trim() || sending) ? 0.5 : 1, flexShrink: 0 }}>
            <Sparkles size={18} color={input.trim() ? '#FFF' : COLORS.textSecondary} />
          </button>
          <button onClick={handleSend} disabled={!input.trim() || sending} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', opacity: (!input.trim() || sending) ? 0.5 : 1, flexShrink: 0 }}>
            <Send size={18} color="#FFF" />
          </button>
        </div>
      </div>
    </div>
  );
}
